import { Injectable } from '@angular/core';
import { CollectionViewer, SelectionChange, DataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Child } from 'app/core/models';
import { IntegrationsService } from '../../../integrations.service';

/** Flat node with expandable and level information */
export class OneDriveFlatNode {
  constructor(
    public item: Child,
    public level = 1,
    public expandable = false,
    public isLoading = false,
    public checkDisabled = false,
    public checked = false,
    public canSync = true,
  ) {}
}

/**
 * Database for one drive data. When expanding a node in the tree, the data source will need to fetch
 * the descendants data from the database.
 */
@Injectable({
  providedIn: 'root',
})
export class OneDriveDatabase {
  private _isSharePointResponseUndefined: boolean = false;

  constructor(private _integrationsService: IntegrationsService) {}

  // If network error, broadcast this info to component class.
  public get sharePointResponse() {
    return this._isSharePointResponseUndefined;
  }

  /** Initial data from database */
  async initialData(): Promise<OneDriveFlatNode[]> {
    const oneDriveRootNode = [];

    // OneDrive files and folders
    oneDriveRootNode.push({
      name: 'OneDrive',
      isSite: true,
      children: [
        {
          name: 'My Files',
          isOneDrive: true,
        },
      ],
    });

    const oneDriveData = await this._integrationsService.loadOneDriveData().toPromise();

    // Sharepoint Sites with files and folders
    const oneDriveRootData = oneDriveData
      ?.map((item) => {
        const children = [
          {
            name: item?.children?.name,
            drive_id: item?.children?.drive_id,
            item_id: item?.children?.item_id,
            site_id: item?.children?.site_id,
            children: [item?.children],
          },
        ];

        return {
          name: item?.name,
          isSite: true,
          children,
        } as Child;
      })
      .filter((item) => {
        return item?.children?.length > 0;
      });

    if (oneDriveRootData) {
      oneDriveRootNode.push(...oneDriveRootData);
    } else {
      this._isSharePointResponseUndefined = true;
    }

    return oneDriveRootNode?.map(
      (node) => new OneDriveFlatNode(node, 0, node.children.length > 0, false, false, false, false),
    );
  }

  async getChildren(node: Child): Promise<Child[] | undefined> {
    if (node.isSite) {
      return node.children;
    }

    const params = {
      drive_id: node.drive_id,
      item_id: node.item_id,
      site_id: node.site_id,
    };

    const oneDriveDataChildren = await this._integrationsService.loadODocumentsChildren(params).toPromise();
    const oneDriveChildren = oneDriveDataChildren.children.map((item) => {
      return {
        site_id: item.site_id,
        drive_id: item.drive_id,
        item_id: item.item_id,
        name: item.name,
        children: [item],
      } as Child;
    });

    return oneDriveChildren;
  }

  isExpandable(node: Child): boolean {
    let isExpandable = false;
    if (node.children && node.children.length) {
      isExpandable = true;
    }
    return isExpandable;
  }
}

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable({
  providedIn: 'root',
})
export class OneDriveDataSource implements DataSource<OneDriveFlatNode> {
  dataChange = new BehaviorSubject<OneDriveFlatNode[]>([]);

  get data(): OneDriveFlatNode[] {
    return this.dataChange.value;
  }
  set data(value: OneDriveFlatNode[]) {
    this._treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(private _treeControl: FlatTreeControl<OneDriveFlatNode>, private _database: OneDriveDatabase) {}

  connect(collectionViewer: CollectionViewer): Observable<OneDriveFlatNode[]> {
    this._treeControl.expansionModel.changed.subscribe((change: SelectionChange<OneDriveFlatNode>) => {
      if (change.added || change.removed) {
        this.handleTreeControl(change);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  disconnect(collectionViewer: CollectionViewer): void {}

  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<OneDriveFlatNode>): void {
    if (change.added) {
      change.added.forEach((node) => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed
        .slice()
        .reverse()
        .forEach((node) => this.toggleNode(node, false));
    }
  }

  /**
   * Toggle the node, remove from display list
   */
  async toggleNode(node: OneDriveFlatNode, expand: boolean) {
    const index = this.data.indexOf(node);

    if (expand) {
      node.isLoading = true;
      const children = await this._database.getChildren(node.item);
      if (children.length > 0 && index >= 0) {
        const nodes = children.map(
          (child) =>
            new OneDriveFlatNode(
              child,
              node.level + 1,
              this._database.isExpandable(child),
              false,
              node.checked || node.checkDisabled,
            ),
        );
        this.data.splice(index + 1, 0, ...nodes);
      } else {
        const noMoreFolders = { name: 'No folders', noMoreFolders: true } as Child;
        const nodes = [new OneDriveFlatNode(noMoreFolders, node.level + 1, this._database.isExpandable(noMoreFolders))];
        this.data.splice(index + 1, 0, ...nodes);
      }
    } else {
      let count = 0;
      for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) {}
      this.data.splice(index + 1, count);

      // if the children removed were selected, we refresh all checkboxes' state
      const selectedNode = this.data.find((n) => n.checked);
      const checkDisabled = this.data.find((n) => n.checkDisabled);
      if (!selectedNode && checkDisabled) {
        this.data.forEach((n) => (n.checkDisabled = false));
      }
    }

    // notify the change
    node.isLoading = false;
    this.dataChange.next(this.data);
  }
}
